import { Box, Button, Stack, Typography, styled } from '@mui/material';

import { ReactComponent as LogoText } from '@/assets/logo-text.svg';

const transition = 'all 0.3s ease-in-out';

const Container = styled(Stack)(() => ({
  margin: '1rem',
  marginRight: 0,
  padding: '0.5rem',
  position: 'sticky',
  top: '1rem',
  zIndex: 1202,
  height: `calc(100vh - 2rem)`,
  backgroundColor: 'white',
  borderRadius: '0.875rem',
  boxShadow: '0 0.0625rem 0.25rem 0.0625rem rgba(0, 0, 0, 0.1)',
}));

const LogoContainer = styled(Stack, { shouldForwardProp: (prop) => prop !== 'isExpanded' })<{
  isExpanded?: boolean | undefined;
}>(({ isExpanded }) => ({
  marginBottom: '1.25rem',
  padding: '0.25rem',
  gap: isExpanded ? 0 : '1rem',
  transition,
  alignItems: 'center',
  flexDirection: isExpanded ? 'row' : 'column',
  cursor: 'pointer',
  justifyContent: 'flex-start',
  width: '100%',

  '& > div': {
    gap: isExpanded ? '0.2rem' : 0,
    flexDirection: 'row',
    alignItems: 'center',
  },
}));

const NavigationContainer = styled(Stack)(() => ({
  flex: 1,
  width: '100%',
  overflowY: 'auto',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
}));

const MenuButton = styled(Button, {
  shouldForwardProp: (prop) => !['isExpanded', 'isActive'].includes(prop.toString()),
})<{
  isExpanded?: boolean | undefined;
  isActive?: boolean | undefined;
}>(({ isExpanded, isActive, theme }) => {
  return {
    borderRadius: '0.75rem',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: '0.62rem',
    width: isExpanded ? '14.5rem' : '2.48rem',
    transition,
    backgroundColor: isActive ? theme.palette.accentExtension.sidemenu : 'transparent',
    overflow: 'hidden',
    '&:hover': {
      backgroundColor: isActive ? theme.palette.accentExtension.sidemenu : theme.palette.grey[50],
    },
  };
});

const ActiveIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  backgroundColor: theme.palette.accent.main,
  width: '0.25rem',
  height: '1.85rem',
  borderRadius: '0 6.25rem 6.25rem 0',
}));

const SubMenu = styled(Stack, {
  shouldForwardProp: (prop) => !['isExpanded'].includes(prop.toString()),
})<{ isExpanded?: boolean | undefined }>(({ isExpanded: _isExpanded }) => ({
  paddingLeft: 0,
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',
  maxWidth: '14.5rem',
  gap: '0.25rem',
  marginTop: '0.25rem',
  transition,
}));

const SubMenuButton = styled(Button, {
  shouldForwardProp: (prop) =>
    !['isActive', 'isStatistics', 'isStatisticsLastDepth', 'isExpanded'].includes(prop.toString()),
})<{
  isActive?: boolean | undefined;
  isStatistics?: boolean | undefined;
  isStatisticsLastDepth?: boolean | undefined;
  isExpanded?: boolean | undefined;
}>(({ theme, isActive, isStatistics, isStatisticsLastDepth, isExpanded }) => ({
  borderRadius: '0.75rem',
  backgroundColor: isActive
    ? theme.palette.accent.light
    : isStatistics
      ? theme.palette.grey[50]
      : 'transparent',
  width: isExpanded ? '100%' : '2.48rem',
  justifyContent: isExpanded ? 'flex-start' : 'center',
  alignItems: 'center',
  padding: isExpanded
    ? isStatistics
      ? '0.25rem 0.62rem 0.25rem 1.12rem'
      : '0.62rem 0.62rem 0.62rem 1.12rem'
    : '0.62rem',
  transition,
  '&:hover': {
    backgroundColor: isActive ? theme.palette.accent.light : theme.palette.grey[50],
  },

  '&  p': {
    ...(isStatisticsLastDepth
      ? {
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          maxWidth: '11.75rem',
        }
      : {
          maxWidth: '11rem',
        }),
  },
}));

const ButtonLabel = styled(Stack, {
  shouldForwardProp: (prop) => !['isActive'].includes(prop.toString()),
})<{
  isActive?: boolean | undefined;
}>(({ theme, isActive }) => {
  return {
    alignItems: 'center',
    flexDirection: 'row',
    display: 'flex',
    flex: 1,
    overflowX: 'hidden',

    '& svg': {
      color: isActive ? theme.palette.accent.main : theme.palette.grey[700],
      width: '1.25rem !important',
      height: '1.25rem !important',
      flexShrink: 0,
    },
  };
});

const ButtonIconWrapper = styled(Stack, {
  shouldForwardProp: (prop) => !['isExpanded'].includes(prop.toString()),
})<{
  isExpanded?: boolean | undefined;
}>(({ isExpanded }) => ({
  gap: isExpanded ? '0.62rem' : '0',
  flexDirection: 'row',
  alignItems: 'center',
  flex: 1,
}));

const ButtonText = styled(Typography, {
  shouldForwardProp: (prop) => !['isExpanded', 'isActive'].includes(prop.toString()),
})<{
  isExpanded?: boolean | undefined;
  isActive?: boolean | undefined;
}>(({ theme, isExpanded, isActive }) => {
  return {
    fontSize: '0.8125rem',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '160%',
    textAlign: 'left',
    width: isExpanded ? '100%' : 0,
    opacity: isExpanded ? 1 : 0,
    transition,

    color: isActive ? theme.palette.accent.main : theme.palette.grey[800],
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  };
});

const Generatik = styled(LogoText, {
  shouldForwardProp: (prop) => prop !== 'isExpanded',
})<{ isExpanded?: boolean | undefined }>(({ isExpanded }) => ({
  width: isExpanded ? '6.14rem' : 0,
  opacity: isExpanded ? 1 : 0,
  height: '1.75rem',
  transition,
}));

const ProfileButtonLabel = styled(Typography, {
  shouldForwardProp: (prop) => !['isExpanded'].includes(prop.toString()),
})<{ isExpanded?: boolean | undefined }>(({ theme, isExpanded }) => ({
  color: theme.palette.grey[800],
  fontSize: '0.875rem',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '150%',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  textAlign: 'left',

  width: isExpanded ? '100%' : 0,
  opacity: isExpanded ? 1 : 0,
  transition,
}));

const AccountMenuWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isExpanded',
})<{ isExpanded?: boolean }>(({ isExpanded, theme }) => ({
  display: 'flex',
  width: isExpanded ? '14.5rem' : 'auto',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  gap: '0.5rem',
  borderRadius: '0.75rem',
  background: isExpanded ? theme.palette.grey[50] : 'transparent',
  transition,
  overflow: 'hidden',
}));

const AccountMenuSeparator = styled(Box)(() => ({
  display: 'flex',
  transform: 'rotate(-0.04deg)',
  padding: '0 8px',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '10px',
  alignSelf: 'stretch',
  animation: 'slideDown 0.2s ease-out forwards',
}));

const AccountMenuDivider = styled(Box)(({ theme }) => ({
  height: '1px',
  transform: 'rotate(0.04deg)',
  alignSelf: 'stretch',
  borderRadius: '19px',
  background: theme.palette.grey[200],
}));

const AccountMenuItemsContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'isExpanded',
})<{ isExpanded?: boolean }>(({ isExpanded }) => ({
  display: 'flex',
  padding: isExpanded ? '1rem 1rem 0 1rem' : 0,
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '4px',
  alignSelf: 'stretch',
  '@keyframes slideDown': {
    '0%': {
      opacity: 0,
      transform: 'translateY(-10px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  animation: 'slideDown 0.2s ease-out forwards',
}));

const AccountMenuItem = styled(Box)(({ theme }) => ({
  padding: '0.125rem 0.5rem',
  borderRadius: '0.5rem',
  cursor: 'pointer',
  transition,
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
}));

const AccountMenuItemText = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[800],
  fontFamily: 'Neue Frutiger World',
  fontSize: '13px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '160%',
  textAlign: 'left',
}));

export {
  Container,
  LogoContainer,
  NavigationContainer,
  MenuButton,
  ActiveIndicator,
  SubMenu,
  SubMenuButton,
  ButtonLabel,
  ButtonIconWrapper,
  ButtonText,
  Generatik,
  ProfileButtonLabel,
  AccountMenuWrapper,
  AccountMenuSeparator,
  AccountMenuDivider,
  AccountMenuItemsContainer,
  AccountMenuItem,
  AccountMenuItemText,
};
